import { effect, inject, Injectable, signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { CartItem, CartItemAddonSelection } from '../models/cartItem.model';
import {
  getAddonSelectionSignature,
  getCartItemLineTotal,
  normalizeCartItemAddons,
} from '../utils/cart-addon.util';
import { UnitService } from './unit.service';
import { MenuService } from './menu.service';
@Injectable({
  providedIn: 'root'
})
export class CartService {
  private cartItemsSubject = new BehaviorSubject<CartItem[]>([]);
  public cartItems$ = this.cartItemsSubject.asObservable();
  routeName = signal<string>('');
  private unitService = inject(UnitService);
  private cartSubject = new BehaviorSubject<{ items: CartItem[]; totalItems: number; totalPrice: number }>({
    items: [],
    totalItems: 0,
    totalPrice: 0
  });
  public cart$ = this.cartSubject.asObservable();
  public cartItemCount$ = this.cart$.pipe(map(cart => cart.totalItems));
  isCartVisible = signal(false);
  cartIconPulse = signal(false);
  private menuService = inject(MenuService);
  private _selectedDate$ = new BehaviorSubject<Date | null>(this.readStoredDate());
  readonly selectedDate$ = this._selectedDate$.asObservable();
  private _selectedCheckoutDate$ = new BehaviorSubject<Date | null>(null);
  /**
   * Last `localStorage.getItem('cart_{route}')` value we know matches in-memory cart.
   * Avoids re-parsing and deep equality checks on every focus/visibility sync when nothing changed.
   */
  private lastKnownCartStorage: { routeName: string; raw: string | null } | null = null;
  constructor() {
    this.routeName.set(this.unitService.routeName());
    this.loadCartFromStorage(this.routeName());
    this.attachCartStorageSyncListeners();
    // Keep cart routeName in sync with current unit (e.g. after success -> home navigation)
    effect(() => {
      const name = this.unitService.routeName();
      if (name) {
        this.routeName.set(name);
        this.loadCartFromStorage(name);
      }
    });
    effect(() => {
      if (!this.menuService.menusLoadComplete()) return;
      this.menuService.menu();
      this.menuService.campaignMenu();
      this.removeInvalidCartItems();
    });
  }

  private loadCartFromStorage(routeName: string): void {
    if (!routeName) {
      return;
    }

    const key = `cart_${routeName}`;
    const raw = localStorage.getItem(key);

    if (
      this.lastKnownCartStorage &&
      this.lastKnownCartStorage.routeName === routeName &&
      this.lastKnownCartStorage.raw === raw
    ) {
      return;
    }

    let items: CartItem[] = [];

    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          items = this.ensureCartLineIds(parsed);
        }
      } catch {
        items = [];
      }
    }

    const prev = this.cartItemsSubject.value;
    this.lastKnownCartStorage = { routeName, raw };

    if (
      prev.length === items.length &&
      (items.length === 0 || JSON.stringify(prev) === JSON.stringify(items))
    ) {
      return;
    }

    this.cartItemsSubject.next(items);
    this.updateCart();

    if (items.length === 0 && prev.length > 0) {
      this.clearDate();
      this.isCartVisible.set(false);
    }
  }

  /**
   * Cross-tab / cross-window: `storage` fires when another document changes localStorage.
   * Same-tab writes do not fire this event.
   */
  private readonly boundOnWindowStorage = (event: StorageEvent): void => {
    if (event.storageArea !== localStorage) {
      return;
    }
    const route = this.routeName();
    if (!route) {
      return;
    }
    const cartKey = `cart_${route}`;
    if (event.key === null || event.key === cartKey) {
      this.loadCartFromStorage(route);
    }
  };

  /**
   * Same-tab storage changes (e.g. DevTools “clear”) do not emit `storage`.
   * Re-read when the user returns to the tab or window so cart/checkout stay in sync.
   */
  private readonly boundOnVisibilityOrFocus = (): void => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      return;
    }
    const route = this.routeName();
    if (route) {
      this.loadCartFromStorage(route);
    }
  };

  /** Register listeners once (root singleton). */
  private attachCartStorageSyncListeners(): void {
    if (typeof window === 'undefined') {
      return;
    }
    window.addEventListener('storage', this.boundOnWindowStorage);
    document.addEventListener('visibilitychange', this.boundOnVisibilityOrFocus);
    window.addEventListener('focus', this.boundOnVisibilityOrFocus);
  }

  private saveCartToStorage(routeName: string): void {
    if (!routeName) {
      return;
    }
    const items = this.cartItemsSubject.value;
    const serialized = JSON.stringify(items);
    localStorage.setItem(`cart_${routeName}`, serialized);
    this.lastKnownCartStorage = { routeName, raw: serialized };
  }

  /** True when product uses stock and is unavailable: stock 0 or stock below minimum order quantity */
  isProductOutOfStock(product: any): boolean {
    if (product?.useStockBalance !== true) return false;
    const stock = product?.stockBalance ?? 0;
    if (stock <= 0) return true;
    const min = product?.minimumOrderQuantity;
    return min != null && min > 0 && stock < min;
  }

  /** True when stock is low (dynamic threshold: stock <= minOrderQty * 2). Only for available products with stock. */
  isFewLeftInStock(product: any): boolean {
    if (product?.useStockBalance !== true) return false;
    const stock = product?.stockBalance ?? 0;
    if (stock <= 0) return false;
    if (this.isProductOutOfStock(product)) return false;
    const min = product?.minimumOrderQuantity ?? 1;
    return stock <= min * 2;
  }

  /** Effective max quantity: stock when useStockBalance, maxOrderQuantity when set; no fallback. */
  getEffectiveMaxQuantity(product: any): number | null {
    const stock = product?.useStockBalance ? (product.stockBalance ?? 0) : null;
    const maxOrder = product?.maxOrderQuantity != null && product.maxOrderQuantity > 0 ? product.maxOrderQuantity : null;
    if (stock !== null && maxOrder !== null) return Math.min(stock, maxOrder);
    if (stock !== null) return stock;
    return maxOrder;
  }

  addToCart(
    product: any,
    quantity: number = 1,
    selectedAddons?: CartItemAddonSelection[]
  ): void {
    const normalizedAddons = normalizeCartItemAddons(selectedAddons);
    const addonSignature = getAddonSelectionSignature(normalizedAddons);
    const currentItems = this.ensureCartLineIds([...this.cartItemsSubject.value]);
    const wasEmpty = currentItems.length === 0;
    const existingItemIndex = currentItems.findIndex(
      (item) =>
        item.product.key === product.key &&
        getAddonSelectionSignature(item.selectedAddons) === addonSignature
    );
    const effectiveMax = this.getEffectiveMaxQuantity(product);

    if (existingItemIndex > -1) {
      const existingItem = currentItems[existingItemIndex];
      const newQty = existingItem.quantity + quantity;
      existingItem.quantity = effectiveMax != null ? Math.min(newQty, effectiveMax) : newQty;
    } else {
      const minQty = product.minimumOrderQuantity ?? 1;
      const qtyToAdd = Math.max(quantity, minQty);
      const capped = effectiveMax != null ? Math.min(qtyToAdd, effectiveMax) : qtyToAdd;
      currentItems.push({
        product,
        quantity: capped,
        selectedAddons: normalizedAddons,
        cartLineId: this.generateCartLineId(),
      });
    }

    this.cartItemsSubject.next(currentItems);
    this.updateCart();
    this.saveCartToStorage(this.routeName());

    if (wasEmpty) {
      this.isCartVisible.set(true);
    } else {
      this.triggerCartIconPulse();
    }
  }


  private triggerCartIconPulse(): void {
    this.cartIconPulse.set(true);
    // Reset after animation completes
    setTimeout(() => {
      this.cartIconPulse.set(false);
    }, 600); // Match this with your CSS animation duration
  }

  removeCartItem(cartLineId: string): void {
    const updatedItems = this.cartItemsSubject.value.filter(
      (item) => item.cartLineId !== cartLineId
    );
    if (updatedItems.length === 0) {
      this.clearDate();
    }
    this.cartItemsSubject.next(updatedItems);
    this.updateCart();
    this.saveCartToStorage(this.routeName());
  }

  removeFromCart(productId: string): void {
    const updatedItems = this.cartItemsSubject.value.filter(
      item => item.product.key !== productId
    );
    if (updatedItems.length === 0) {
      this.clearDate();
    }
    this.cartItemsSubject.next(updatedItems);
    this.updateCart();
    this.saveCartToStorage(this.routeName());
  }

  updateCartItemQuantity(cartLineId: string, quantity: number): void {
    const currentItems = [...this.cartItemsSubject.value];
    const itemIndex = currentItems.findIndex(
      (item) => item.cartLineId === cartLineId
    );

    if (itemIndex > -1) {
      if (quantity <= 0) {
        this.removeCartItem(cartLineId);
      } else {
        const product = currentItems[itemIndex].product;
        const effectiveMax = this.getEffectiveMaxQuantity(product);
        const capped = effectiveMax != null ? Math.min(quantity, effectiveMax) : quantity;
        currentItems[itemIndex].quantity = capped;
        this.cartItemsSubject.next(currentItems);
        this.updateCart();
        this.saveCartToStorage(this.routeName());
      }
    }
  }

  updateQuantity(productId: string, quantity: number): void {
    const item = this.cartItemsSubject.value.find(
      (entry) => entry.product.key === productId
    );
    if (item?.cartLineId) {
      this.updateCartItemQuantity(item.cartLineId, quantity);
    }
  }

  clearCart(routeName?: string): void {
    const route = routeName || this.routeName();
    this.cartItemsSubject.next([]);
    this.clearDate()
    this.updateCart();
    this.saveCartToStorage(route);
  }

  getCartItemCount(): number {
    return this.cartSubject.value.totalItems;
  }

  getCartTotal(): number {
    return this.cartSubject.value.totalPrice;
  }

  private updateCart(): void {
    const items = this.cartItemsSubject.value;
    const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
    const totalPrice = items.reduce(
      (sum, item) => sum + getCartItemLineTotal(item),
      0
    );
    this.cartSubject.next({ items, totalItems, totalPrice });
  }

  openCart() {
    this.isCartVisible.set(true);
  }

  closeCart() {
    this.isCartVisible.set(false);
  }

  toggleCart() {
    this.isCartVisible.update((v: any) => !v);
  }

  private getAllProductsFromMenus(): Map<string, any> {
    const productMap = new Map<string, any>();

    const menu = this.menuService.menu();
    const regularMenuActive = !menu?.event || menu.event.status === 'active';
    if (menu?.categories && regularMenuActive) {
      menu.categories.forEach((cat: any) => {
        cat.products?.forEach((product: any) => {
          if (product.key) {
            productMap.set(product.key, product);
          }
        });
      });
    }

    const campaignMenu = this.menuService.campaignMenu();
    if (campaignMenu?.categories && campaignMenu.campaign?.status === 'active') {
      campaignMenu.categories.forEach((cat: any) => {
        cat.products?.forEach((product: any) => {
          if (product.key) {
            // Campaign menu products take precedence if they exist
            productMap.set(product.key, product);
          }
        });
      });
    }

    return productMap;
  }

  removeInvalidCartItems(): void {
    const allProducts = this.getAllProductsFromMenus();
    const validProductKeys = new Set(allProducts.keys());
    const currentItems = this.cartItemsSubject.value;
    let hasChanges = false;

    const updatedItems = currentItems
      .filter(item => {
        if (!validProductKeys.has(item.product.key)) {
          hasChanges = true;
          return false;
        }
        return true;
      })
      .map(item => {
        const latestProduct = allProducts.get(item.product.key);
        if (latestProduct) {
          const productChanged =
            item.product.minimumOrderLeadDays !== latestProduct.minimumOrderLeadDays ||
            item.product.cost !== latestProduct.cost ||
            item.product.minimumOrderQuantity !== latestProduct.minimumOrderQuantity ||
            item.product.maxOrderQuantity !== latestProduct.maxOrderQuantity ||
            item.product.name !== latestProduct.name ||
            item.product.description !== latestProduct.description ||
            item.product.imageUrl !== latestProduct.imageUrl ||
            item.product.enabled !== latestProduct.enabled;

          if (productChanged) {
            hasChanges = true;
            return {
              ...item,
                product: { ...latestProduct } // Create new object to ensure reactivity
              };
          }
        }
        return item;
      });

    if (hasChanges) {
      this.cartItemsSubject.next(updatedItems);
      this.updateCart();
      this.saveCartToStorage(this.routeName());
    }
  }
  setDate(date: Date) {
    this._selectedDate$.next(date);
    this.setCheckoutDate(date);
    localStorage.setItem('deliveryDate', date.toISOString());
  }

  getDate(): Date | null {
    return this._selectedDate$.value;
  }

  clearDate() {
    this._selectedDate$.next(null);
    localStorage.removeItem('deliveryDate');
    this.clearCheckoutDate();
  }
  setCheckoutDate(date: Date) {
    this._selectedCheckoutDate$.next(date);
    localStorage.setItem('deliveryCheckoutDate', date.toISOString());
  }

  getCheckoutDate(): Date | null {
    const stored = localStorage.getItem('deliveryCheckoutDate');
    if (stored) return new Date(stored);
    return this._selectedCheckoutDate$.value;
  }

  clearCheckoutDate() {
    this._selectedCheckoutDate$.next(null);
    localStorage.removeItem('deliveryCheckoutDate');
  }

  private generateCartLineId(): string {
    return `line-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }

  private ensureCartLineIds(items: CartItem[]): CartItem[] {
    return items.map((item) => ({
      ...item,
      cartLineId: item.cartLineId ?? this.generateCartLineId(),
    }));
  }

  private readStoredDate(): Date | null {
    const stored = localStorage.getItem('deliveryDate');
    return stored ? new Date(stored) : null;
  }

  getCartItemsUnavailableForSelectedDate(): CartItem[] {
    const selectedDate = this.getDate();
    if (!selectedDate) return [];

    const items = this.cartItemsSubject.value;
    if (!items.length) return [];

    const { campaignItems, nonCampaignItems } =
      this.menuService.splitCampaignAndNonCampaignItems(items);
    const servingMethod = this.getHomeServingMethod(this.routeName()) ?? 'pickup';

    const pickUnavailable = (
      bucket: CartItem[],
      menuType: 'campaign' | 'home',
      menu: any
    ): CartItem[] => {
      if (!menu || !bucket.length) return [];
      return bucket.filter(item => {
        const prepared = this.menuService.prepareProduct(
          item.product,
          selectedDate,
          menuType,
          menu,
          servingMethod
        );
        return prepared?.isAvailableOnSelectedDate === false;
      });
    };

    return [
      ...pickUnavailable(campaignItems, 'campaign', this.menuService.campaignMenu()),
      ...pickUnavailable(nonCampaignItems, 'home', this.menuService.menu())
    ];
  }

  saveHomeServingMethod(routeName: string, method: 'pickup' | 'delivery') {
    localStorage.setItem(`homeServingMethod_${routeName}`, method);
  }
  clearHomeServingMethod(routeName: string) {
    localStorage.removeItem(`homeServingMethod_${routeName}`);
  }
  getHomeServingMethod(routeName: string): 'pickup' | 'delivery' | null {
    const stored = localStorage.getItem(`homeServingMethod_${routeName}`);
    if (stored) return stored as 'pickup' | 'delivery';
    return null;
  }
}
